import React, { useState, useEffect } from "react";
import SubscriptionService from "../services/SubscriptionService";
import DataTable from "../components/DataTable";
import Loader from "../components/Loader";
import { toast } from "react-toastify";
import { FiUsers, FiCalendar, FiStar, FiActivity } from "react-icons/fi";

const UnsubscribedDrivers = () => {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedDrivers, setSelectedDrivers] = useState([]);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [filters, setFilters] = useState({
    search: "",
    minRating: "",
    lastActive: "",
  });

  useEffect(() => {
    loadUnsubscribedDrivers();
  }, [currentPage, filters]);

  const loadUnsubscribedDrivers = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: 20,
        ...filters,
      };

      const response = await SubscriptionService.getUnsubscribedDrivers(params);
      setDrivers(response.drivers);
      setTotalPages(response.pagination.totalPages);
    } catch (error) {
      console.error("Error loading unsubscribed drivers:", error);
      toast.error("Failed to load unsubscribed drivers");
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
    setCurrentPage(1);
  };

  const handleSendBulkReminders = async () => {
    if (selectedDrivers.length === 0) {
      toast.warning("Please select drivers to send reminders to");
      return;
    }

    const message = prompt(
      "Enter message for bulk reminders:",
      "Join our subscription plan and start earning more with unlimited rides!"
    );
    if (!message) return;

    try {
      // Since there's no bulk reminder API for unsubscribed drivers,
      // we'll simulate it or you can implement it based on your backend
      toast.info(`Sending reminders to ${selectedDrivers.length} drivers...`);

      // Here you would typically call a bulk messaging API
      // For now, we'll just show a success message
      toast.success(
        `Reminders sent to ${selectedDrivers.length} drivers successfully`
      );
      setSelectedDrivers([]);
    } catch (error) {
      console.error("Error sending bulk reminders:", error);
      toast.error("Failed to send reminders");
    }
  };

  const handleExportDrivers = async () => {
    try {
      // Create CSV data for unsubscribed drivers
      const csvData = drivers.map((driver) => ({
        "Driver ID": driver._id,
        "Full Name": driver.fullName,
        Phone: driver.phone,
        Email: driver.email || "N/A",
        "Total Rides": driver.totalRides || 0,
        Rating: driver.rating || "N/A",
        "Last Active": driver.lastActiveAt
          ? new Date(driver.lastActiveAt).toLocaleDateString()
          : "N/A",
        Status: driver.isOnline ? "Online" : "Offline",
        "Created Date": new Date(driver.createdAt).toLocaleDateString(),
      }));

      const csvContent = convertToCSV(csvData);
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `unsubscribed_drivers_${new Date().toISOString().split("T")[0]}.csv`
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success(`Exported ${csvData.length} unsubscribed drivers`);
    } catch (error) {
      console.error("Error exporting drivers:", error);
      toast.error("Failed to export drivers");
    }
  };

  const convertToCSV = (data) => {
    if (!data || data.length === 0) return "";

    const headers = Object.keys(data[0]);
    const csvHeaders = headers.join(",");
    const csvRows = data.map((row) =>
      headers
        .map((header) => {
          const value = row[header];
          return typeof value === "string" &&
            (value.includes(",") || value.includes('"'))
            ? `"${value.replace(/"/g, '""')}"`
            : value;
        })
        .join(",")
    );

    return [csvHeaders, ...csvRows].join("\n");
  };

  const getOnlineStatusBadge = (isOnline) => {
    return (
      <span
        className={`px-2 py-1 text-xs font-medium rounded-full ${
          isOnline ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"
        }`}
      >
        {isOnline ? "Online" : "Offline"}
      </span>
    );
  };

  const columns = [
    {
      Header: "Driver",
      accessor: "fullName",
      Cell: ({ value: fullName, row: { original: driver } }) => (
        <div>
          <div className="font-medium text-gray-900">{fullName || "N/A"}</div>
          <div className="text-sm text-gray-500">{driver?.phone || "N/A"}</div>
          {driver?.email && (
            <div className="text-xs text-gray-400">{driver.email}</div>
          )}
        </div>
      ),
    },
    {
      Header: "Status",
      accessor: "isOnline",
      Cell: ({ value: isOnline }) => getOnlineStatusBadge(isOnline),
    },
    {
      Header: "Total Rides",
      accessor: "totalRides",
      Cell: ({ value: totalRides }) => totalRides || 0,
    },
    {
      Header: "Rating",
      accessor: "rating",
      Cell: ({ value: rating }) => (
        <div className="flex items-center">
          <FiStar className="text-yellow-400 mr-1" size={14} />
          <span>{rating ? rating.toFixed(1) : "N/A"}</span>
        </div>
      ),
    },
    {
      Header: "Last Active",
      accessor: "lastActiveAt",
      Cell: ({ value: lastActiveAt }) =>
        lastActiveAt ? new Date(lastActiveAt).toLocaleDateString() : "Never",
    },
    {
      Header: "Joined",
      accessor: "createdAt",
      Cell: ({ value: createdAt }) => new Date(createdAt).toLocaleDateString(),
    },
    {
      Header: "Actions",
      accessor: "_id",
      Cell: ({ value: id, row: { original: driver } }) => (
        <div className="flex space-x-2">
          <button
            onClick={() => {
              const message = prompt(
                `Send message to ${driver?.fullName || "Driver"}:`,
                "Start earning more with our subscription plans!"
              );
              if (message) {
                toast.success(
                  `Message sent to ${driver?.fullName || "Driver"}`
                );
              }
            }}
            className="text-blue-600 hover:text-blue-900 text-sm"
          >
            Send Message
          </button>
          <button
            onClick={() => {
              // Navigate to driver detail or show more info
              toast.info("Driver detail view coming soon");
            }}
            className="text-gray-600 hover:text-gray-900 text-sm"
          >
            View Details
          </button>
        </div>
      ),
    },
  ];

  if (loading && !drivers.length) {
    return <Loader />;
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Unsubscribed Drivers
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage drivers who haven't subscribed to any plans
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <FiUsers className="text-gray-500" />
          <span className="text-sm text-gray-600">
            {drivers.length} drivers
          </span>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center">
            <FiUsers className="text-blue-500 mr-3" size={24} />
            <div>
              <div className="text-sm font-medium text-gray-500">
                Total Drivers
              </div>
              <div className="text-2xl font-bold text-gray-900">
                {drivers.length}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center">
            <FiActivity className="text-green-500 mr-3" size={24} />
            <div>
              <div className="text-sm font-medium text-gray-500">
                Online Now
              </div>
              <div className="text-2xl font-bold text-green-600">
                {drivers.filter((d) => d.isOnline).length}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center">
            <FiStar className="text-yellow-500 mr-3" size={24} />
            <div>
              <div className="text-sm font-medium text-gray-500">
                Avg Rating
              </div>
              <div className="text-2xl font-bold text-yellow-600">
                {drivers.length > 0
                  ? (
                      drivers.reduce((sum, d) => sum + (d.rating || 0), 0) /
                      drivers.length
                    ).toFixed(1)
                  : "0.0"}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center">
            <FiCalendar className="text-purple-500 mr-3" size={24} />
            <div>
              <div className="text-sm font-medium text-gray-500">
                Active Today
              </div>
              <div className="text-2xl font-bold text-purple-600">
                {
                  drivers.filter((d) => {
                    if (!d.lastActiveAt) return false;
                    const lastActive = new Date(d.lastActiveAt);
                    const today = new Date();
                    return lastActive.toDateString() === today.toDateString();
                  }).length
                }
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Filters</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search
            </label>
            <input
              type="text"
              placeholder="Search by name, phone, email..."
              value={filters.search}
              onChange={(e) => handleFilterChange("search", e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Minimum Rating
            </label>
            <select
              value={filters.minRating}
              onChange={(e) => handleFilterChange("minRating", e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            >
              <option value="">All Ratings</option>
              <option value="1">1+ Stars</option>
              <option value="2">2+ Stars</option>
              <option value="3">3+ Stars</option>
              <option value="4">4+ Stars</option>
              <option value="4.5">4.5+ Stars</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Last Active
            </label>
            <select
              value={filters.lastActive}
              onChange={(e) => handleFilterChange("lastActive", e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            >
              <option value="">All Time</option>
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
            </select>
          </div>
        </div>
      </div>

      {/* Actions Bar */}
      <div className="bg-white p-4 rounded-lg shadow flex justify-between items-center">
        <div className="flex space-x-4">
          <button
            onClick={() => setShowBulkActions(!showBulkActions)}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
          >
            Bulk Actions
          </button>
          <button
            onClick={handleExportDrivers}
            className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700"
          >
            Export CSV
          </button>
          <button
            onClick={loadUnsubscribedDrivers}
            className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700"
          >
            Refresh
          </button>
        </div>
        {selectedDrivers.length > 0 && (
          <div className="text-sm text-gray-600">
            {selectedDrivers.length} selected
          </div>
        )}
      </div>

      {/* Bulk Actions */}
      {showBulkActions && (
        <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
          <div className="flex space-x-4">
            <button
              onClick={handleSendBulkReminders}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
              disabled={selectedDrivers.length === 0}
            >
              Send Bulk Messages ({selectedDrivers.length})
            </button>
          </div>
        </div>
      )}

      {/* Data Table */}
      <div className="bg-white rounded-lg shadow">
        <DataTable
          columns={columns}
          data={drivers}
          loading={loading}
          selectable={showBulkActions}
          selectedItems={selectedDrivers}
          onSelectionChange={setSelectedDrivers}
        />

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200">
            <div className="flex justify-between items-center">
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 disabled:opacity-50"
              >
                Previous
              </button>
              <span className="text-sm text-gray-700">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UnsubscribedDrivers;
